import type { FoundryV2EclosiaBenchmarkRecord } from "../api/foundryV2Client";

function statusTone(status: "ready" | "watch" | "blocked"): string {
  if (status === "ready") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "watch") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-red-200 bg-red-50 text-red-700";
}

function verdictTone(verdict: "go_firm" | "go_conditional" | "no_go"): string {
  if (verdict === "go_firm") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (verdict === "go_conditional") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-red-200 bg-red-50 text-red-700";
}

type Props = {
  benchmark: FoundryV2EclosiaBenchmarkRecord | null;
  isLoading: boolean;
  error: string | null;
};

export function FoundryEclosiaBenchmarkPanel({ benchmark, isLoading, error }: Props): JSX.Element {
  const report = benchmark?.report ?? null;
  const artifactUrls = benchmark?.artifactUrls ?? null;

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#fffef7_0%,#f8fbff_100%)] p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]"
      data-testid="foundry-eclosia-benchmark-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
            Eclosia Supremacy Benchmark
          </div>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">
            Hard benchmark against Conciergerie 2.0 on the bounded dispatch-core lane
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            The benchmark stays explicit about what LongWalls beats, where it still trails the manual baseline, and
            whether the outcome is a firm GO, a conditional GO, or a NO-GO.
          </p>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {isLoading && !benchmark ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Loading Eclosia benchmark...
        </div>
      ) : null}

      {report ? (
        <>
          <div className="mt-5 grid gap-3 xl:grid-cols-[1.2fr_0.8fr_0.8fr_0.8fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusTone(report.summary.status)}`}>
                  {report.summary.status}
                </span>
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${verdictTone(report.summary.verdict)}`}>
                  {report.summary.verdict.replace(/_/g, " ")}
                </span>
                <span className="text-xs text-slate-500">Engine {report.summary.engineScore}/{report.summary.threshold}</span>
              </div>
              <div className="mt-3 text-sm text-slate-700">{report.summary.headline}</div>
              <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-2">
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  Gap to manual: {report.summary.gapToManual}
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {report.comparison.absorbedLotCount}/{report.comparison.totalLotCount} absorbed lots
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {report.summary.readyAxisCount} ready axis(es)
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {report.summary.watchAxisCount} watch / {report.summary.blockedAxisCount} blocked
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Baseline</div>
              <div className="mt-3 text-sm font-semibold text-slate-900">{report.benchmark.baselineApplication}</div>
              <div className="mt-2 text-xs text-slate-600">{report.benchmark.repoPath}</div>
              <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Surface: {report.benchmark.comparisonSurface}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Weighted totals</div>
              <div className="mt-3 space-y-2 text-xs text-slate-600">
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">Manual {report.comparison.manualExisting}/100</div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">Blind {report.comparison.engineBlind}/100</div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  Reuse {report.comparison.engineReuseBaseline}/100
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  Current {report.comparison.engineCurrent}/100
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Artifacts</div>
              <div className="mt-3 space-y-2 text-xs">
                <a className="block font-medium text-blue-700 underline" href={artifactUrls?.benchmarkReport ?? "#"}>
                  eclosia_supremacy_benchmark.v1.json
                </a>
                <a className="block font-medium text-blue-700 underline" href={artifactUrls?.benchmarkSummary ?? "#"}>
                  eclosia_supremacy_benchmark.v1.md
                </a>
                {report.artifactUrls.blindScorecard ? (
                  <a className="block font-medium text-blue-700 underline" href={report.artifactUrls.blindScorecard}>
                    eclosia_blind_rebuild_scorecard.json
                  </a>
                ) : null}
                {report.artifactUrls.comparisonScorecard ? (
                  <a className="block font-medium text-blue-700 underline" href={report.artifactUrls.comparisonScorecard}>
                    eclosia_engine_vs_manual_scorecard.md
                  </a>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Benchmark axes</div>
                <div className="text-xs text-slate-500">{report.benchmark.parityMode.replace(/_/g, " ")}</div>
              </div>
              <div className="mt-3 space-y-3">
                {report.axes.map((axis) => (
                  <div key={axis.id} className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{axis.label}</div>
                        <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          {axis.formula}
                        </div>
                      </div>
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusTone(axis.status)}`}>
                        {axis.status}
                      </span>
                    </div>
                    <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-3">
                      <div className="rounded-xl border border-slate-100 bg-white px-3 py-2">Manual {axis.manualScore}/100</div>
                      <div className="rounded-xl border border-slate-100 bg-white px-3 py-2">Engine {axis.engineScore}/100</div>
                      <div className="rounded-xl border border-slate-100 bg-white px-3 py-2">Gap {axis.gapToManual}</div>
                    </div>
                    <div className="mt-3 text-xs text-slate-600">{axis.rationale}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Input posture</div>
                <div className="mt-3 grid gap-2 text-xs text-slate-600">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                    Delivery graph: {report.benchmarkInputs.deliveryGraph.status} / coverage {Math.round(report.benchmarkInputs.deliveryGraph.coverageScore * 100)}
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                    QA factory: {report.benchmarkInputs.qaFactory.status} / coverage {Math.round(report.benchmarkInputs.qaFactory.coverageScore * 100)}
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                    Jarvis evaluation: {report.benchmarkInputs.jarvisEvaluation.status} / score {report.benchmarkInputs.jarvisEvaluation.score}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Conditions</div>
                <div className="mt-3 space-y-2 text-xs text-slate-600">
                  {report.conditions.length ? (
                    report.conditions.map((item) => (
                      <div key={item} className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                        {item}
                      </div>
                    ))
                  ) : (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">No condition remains.</div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Blockers and next actions</div>
                <div className="mt-3 space-y-2 text-xs text-slate-600">
                  {report.blockers.length ? (
                    report.blockers.map((item) => (
                      <div key={item} className="rounded-xl border border-red-200 bg-red-50 px-3 py-2">
                        {item}
                      </div>
                    ))
                  ) : (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                      No blocking gap remains inside the bounded benchmark lane.
                    </div>
                  )}
                  {report.nextActions.map((item) => (
                    <div key={item} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
